/* TAB 5 — Trade Journal + Performance dashboard.
   Trades are persisted in MongoDB (per-user) via /api/trading/trades.
   Replaces the prior SAMPLE_TRADES + localStorage approach so trades
   survive cache clears and follow the user across browsers/devices.

   Stop-Loss Monitor: every 30s during market hours, this tab polls live
   prices for every Open trade and compares to its stop. If the stop is
   breached, the trade is marked "Stop Hit" automatically AND a Telegram
   alert fires immediately. A flashing red banner surfaces the breach in
   the UI. */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { C, Spark } from "./_shared";
import { useCompanyNames } from "./_companyNames";
import { useSubTab } from "./_useSubTab";

const JOURNAL_KEY_TO_SLUG = { journal: "journal", performance: "performance" };
const JOURNAL_SLUG_TO_KEY = { journal: "journal", performance: "performance" };

// Hoisted form-row + select-row helpers — defined at module scope and
// memoised so React reuses the same DOM input across re-renders. When these
// were declared INSIDE the parent component, every keystroke produced a new
// component reference → React unmounted-and-remounted every input → focus
// died after each digit. Same root cause as the OptionsTradePlanner typing
// bug. type="text" + inputMode="decimal" gives the numeric mobile keyboard
// without Safari/Chrome's number-input quirks (scroll-wheel value mutation,
// decimal-point loss).
// Session-1 redesign tokens — local so we don't bleed into _shared.jsx
const T = {
  card:       { background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:8, padding:"16px 20px", marginBottom:0, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" },
  sectionLab: { fontSize:10, fontWeight:500, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 },
  inputLab:   { fontSize:10, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:3, fontWeight:500 },
  input:      { padding:"7px 10px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, background:"#fff" },
  primaryBtn: { background:"#4f46e5", color:"#fff", border:"none", borderRadius:6, padding:"8px 18px", fontSize:13, fontWeight:600, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6 },
  secondaryBtn: { background:"#fff", color:"#64748b", border:"1px solid #e2e8f0", borderRadius:6, padding:"6px 14px", fontSize:12, cursor:"pointer" },
  badgePos:   { background:"#dcfce7", color:"#16a34a", fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4 },
  badgeNeg:   { background:"#fee2e2", color:"#dc2626", fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4 },
  badgeNeu:   { background:"#f1f5f9", color:"#64748b", fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4 },
  badgeIndigo:{ background:"#e0e7ff", color:"#4f46e5", fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4 },
  statCard:   { background:"#f9fafb", borderRadius:7, padding:"10px 12px", border:"1px solid #e2e8f0" },
};
const INPUT_WIDTH = { date:120, time:80, ticker:80, entry:90, exit:90, stop:90, shares:80, strategy:140, session:100, grade:80 };

const FormInput = React.memo(function FormInput({ label, fieldKey, value, type, ph, onChange }) {
  const useType = type === "number" ? "text" : (type || "text");
  const w = INPUT_WIDTH[fieldKey];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0, width: w ? w : undefined }}>
      <label style={T.inputLab}>{label}</label>
      <input
        type={useType}
        inputMode={type === "number" ? "decimal" : undefined}
        autoComplete="off"
        value={value}
        onChange={e => onChange(fieldKey, e.target.value)}
        placeholder={ph}
        data-testid={`journal-input-${fieldKey}`}
        style={{ ...T.input, width: w ? w : undefined }}
      />
    </div>
  );
});

const FormSelect = React.memo(function FormSelect({ label, fieldKey, value, opts, onChange }) {
  const w = INPUT_WIDTH[fieldKey];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0, width: w ? w : undefined }}>
      <label style={T.inputLab}>{label}</label>
      <select
        value={value}
        onChange={e => onChange(fieldKey, e.target.value)}
        data-testid={`journal-select-${fieldKey}`}
        style={{ ...T.input, width: w ? w : undefined }}
      >
        {opts.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
});

const API = process.env.REACT_APP_BACKEND_URL || "";
const STOP_MONITOR_INTERVAL = 30 * 1000; // 30s

function isMarketHours() {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", weekday: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = fmt.formatToParts(new Date()).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  if (parts.weekday === "Sat" || parts.weekday === "Sun") return false;
  const t = parseInt(parts.hour, 10) * 60 + parseInt(parts.minute, 10);
  return t >= 570 && t < 960; // 9:30am — 4:00pm ET
}

async function apiJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  if (res.status === 204) return null;
  const txt = await res.text();
  return txt ? JSON.parse(txt) : null;
}

export default function JournalTab({ journalPrefill, clearJournalPrefill }) {
  const EMPTY = {date:"",time:"",ticker:"",strategy:"Momentum + VWAP",session:"Open",entry:"",exit:"",stop:"",shares:"",grade:"B",notes:""};
  const [trades,  setTrades]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [form,    setForm]    = useState(EMPTY);
  const [editId,  setEditId]  = useState(null);
  const [view, setView] = useSubTab("journal", {
    slugToKey: JOURNAL_SLUG_TO_KEY,
    keyToSlug: JOURNAL_KEY_TO_SLUG,
    parentSlug: "journal",
  });
  const [sessF,   setSessF]   = useState("All");
  const { name: companyName } = useCompanyNames();

  // Pre-fill from a TickerLink right-click "Log a trade" OR from
  // Trade Plan's "Log to Journal" button (which passes a richer
  // payload including entry/stop/shares/rationale).
  useEffect(() => {
    if (!journalPrefill?.ticker) return;
    setForm(f => ({
      ...f,
      ticker: journalPrefill.ticker,
      date:   f.date || new Date().toISOString().slice(0, 10),
      entry:  journalPrefill.entry  != null && journalPrefill.entry  !== "" ? String(journalPrefill.entry)  : f.entry,
      stop:   journalPrefill.stop   != null && journalPrefill.stop   !== "" ? String(journalPrefill.stop)   : f.stop,
      shares: journalPrefill.shares != null && journalPrefill.shares !== "" ? String(journalPrefill.shares) : f.shares,
      notes:  journalPrefill.rationale
                ? [
                    `From Trade Plan${journalPrefill.target1 != null ? ` — T1 ${journalPrefill.target1}` : ""}${journalPrefill.target2 != null && journalPrefill.target2 !== "" ? ` · T2 ${journalPrefill.target2}` : ""}${journalPrefill.rr != null ? ` · R:R ${Number(journalPrefill.rr).toFixed(2)}` : ""}`,
                    journalPrefill.rationale,
                    f.notes,
                  ].filter(Boolean).join("\n")
                : f.notes,
    }));
    if (typeof clearJournalPrefill === "function") clearJournalPrefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalPrefill?.ticker]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await apiJSON("/api/trading/trades");
      setTrades(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || "Failed to load trades");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Stop Loss Monitor ──────────────────────────────────────
  // Polls live Polygon prices for every Open trade with a stop, every 30s,
  // during market hours. If price <= stop, marks the trade "Stop Hit" in
  // MongoDB AND fires a Telegram alert. Per-trade dedupe via a Set ref so
  // the same trade never alerts twice.
  const stopAlerted = useRef(new Set()); // trade ids already alerted this session
  const [stopBanner, setStopBanner] = useState(null); // { ticker, current, stop }

  useEffect(() => {
    if (!isMarketHours()) return undefined;
    const openTrades = trades.filter(t => t.status === "Open" && t.stop > 0);
    if (openTrades.length === 0) return undefined;

    let cancelled = false;
    const tick = async () => {
      if (!isMarketHours() || cancelled) return;
      const tickers = [...new Set(openTrades.map(t => t.ticker))];
      try {
        const res = await fetch(`${API}/api/stock/snapshot?tickers=${encodeURIComponent(tickers.join(","))}`, { credentials: "include" });
        const data = await res.json();
        const priceMap = {};
        for (const r of (data?.results || [])) priceMap[r.ticker] = r.price;
        for (const tr of openTrades) {
          const cur = priceMap[tr.ticker];
          if (!cur || stopAlerted.current.has(tr.id)) continue;
          if (cur <= tr.stop) {
            stopAlerted.current.add(tr.id);
            setStopBanner({ ticker: tr.ticker, current: cur, stop: tr.stop, id: tr.id });
            // Compute P&L if we know entry + shares
            const entryPx = +tr.entry || 0;
            const shares  = +tr.shares || 0;
            const pnl     = entryPx && shares ? (cur - entryPx) * shares : 0;
            const pnlLine = entryPx && shares
              ? `Trade P&L: ${pnl >= 0 ? "+" : ""}$${pnl.toFixed(0)}`
              : "";
            const name = companyName(tr.ticker, "");
            const header = name ? `${tr.ticker} · ${name}` : tr.ticker;
            const message = [
              `🔴 <b>STOP LOSS HIT — STOCK</b>`,
              `Ticker: <b>${header}</b>`,
              `Your stop: $${tr.stop.toFixed(2)}`,
              `Current price: $${cur.toFixed(2)}`,
              ...(pnlLine ? [pnlLine] : []),
              `──────────────`,
              `Action: EXIT NOW`,
              `Session: ${tr.session || "—"}`,
            ].join("\n");
            try {
              await fetch(`${API}/api/stock/alert/telegram`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message }),
              });
            } catch { /* noop */ }
            // Mark trade as Stop Hit in MongoDB
            try {
              const updated = await apiJSON(`/api/trading/trades/${tr.id}`, {
                method: "PUT",
                body: JSON.stringify({ ...tr, status: "Stop Hit", exit: cur }),
              });
              setTrades(p => p.map(x => x.id === tr.id ? updated : x));
            } catch { /* noop */ }
          }
        }
      } catch { /* noop */ }
    };
    tick();
    const id = setInterval(tick, STOP_MONITOR_INTERVAL);
    return () => { cancelled = true; clearInterval(id); };
  }, [trades, companyName]);

  async function save() {
    if (!form.date||!form.ticker||!form.entry||!form.shares) return;
    const body = {
      date: form.date,
      time: form.time || "—",
      ticker: form.ticker.toUpperCase(),
      strategy: form.strategy,
      session: form.session,
      entry: +form.entry,
      exit: +form.exit || 0,
      stop: +form.stop || 0,
      shares: +form.shares,
      status: form.exit ? "Closed" : "Open",
      grade: form.grade,
      notes: form.notes,
    };
    setSaving(true);
    setError("");
    try {
      if (editId) {
        const updated = await apiJSON(`/api/trading/trades/${editId}`, { method: "PUT", body: JSON.stringify(body) });
        setTrades(p => p.map(x => x.id === editId ? updated : x));
        setEditId(null);
      } else {
        const created = await apiJSON("/api/trading/trades", { method: "POST", body: JSON.stringify(body) });
        setTrades(p => [...p, created]);
      }
      setForm(EMPTY);
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }
  async function del(id) {
    if (!window.confirm("Delete this trade?")) return;
    try {
      await apiJSON(`/api/trading/trades/${id}`, { method: "DELETE" });
      setTrades(p => p.filter(x => x.id !== id));
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  }
  function edit(t) { setEditId(t.id); setForm({...t,exit:t.exit||"",stop:t.stop||""}); }

  const closed   = trades.filter(t=>t.status==="Closed" || t.status==="Stop Hit");
  const filtered = sessF==="All"?closed:closed.filter(t=>t.session===sessF);
  const pnls     = filtered.map(t=>(t.exit-t.entry)*t.shares);
  const total    = pnls.reduce((a,b)=>a+b,0);
  const wins     = pnls.filter(p=>p>0);
  const losses   = pnls.filter(p=>p<0);
  const winRate  = filtered.length?Math.round(wins.length/filtered.length*100):0;
  const avgW     = wins.length?wins.reduce((a,b)=>a+b,0)/wins.length:0;
  const avgL     = losses.length?Math.abs(losses.reduce((a,b)=>a+b,0)/losses.length):0;
  const pf       = avgL?(wins.reduce((a,b)=>a+b,0)/Math.abs(losses.reduce((a,b)=>a+b,0))).toFixed(2):"∞";
  const rr       = avgL?(avgW/avgL).toFixed(2):"—";

  let eq=10000;
  const eqPts=[10000];
  filtered.forEach(t=>eqPts.push(eq+=(t.exit-t.entry)*t.shares));
  const eqUp=eqPts[eqPts.length-1]>=10000;

  const byStrat={};
  filtered.forEach(t=>{
    if(!byStrat[t.strategy])byStrat[t.strategy]={w:0,n:0,pnl:0};
    const p=(t.exit-t.entry)*t.shares;
    byStrat[t.strategy].n++;
    if(p>0)byStrat[t.strategy].w++;
    byStrat[t.strategy].pnl+=p;
  });

  const gradeC={"A+":C.green,A:C.green,"B+":C.teal,B:C.blue,C:C.amber,D:C.red,F:C.red};
  // Stable callback for FormInput/FormSelect — referential equality keeps
  // React.memo working so a keystroke in one field doesn't re-render the
  // others.
  const handleFormChange = useCallback((k, v) => {
    setForm(f => ({ ...f, [k]: v }));
  }, []);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      <div style={{ display:"flex", gap:4, marginBottom:4 }}>
        {[{k:"journal",l:"Journal"},{k:"performance",l:"Performance"}].map(v=>(
          <button key={v.k} onClick={()=>setView(v.k)} data-testid={`journal-view-${v.k}`}
            style={{
              fontSize:12, fontWeight:500, padding:"6px 14px", borderRadius:6, border:"none", cursor:"pointer",
              background: view===v.k ? "#f1f5f9" : "transparent",
              color:      view===v.k ? "#0f172a" : "#64748b",
            }}>
            {v.l}
          </button>
        ))}
      </div>

      {error && (
        <div data-testid="journal-error" style={{background:C.rb, color:C.rt, padding:"8px 12px", borderRadius:8, fontSize:11, fontWeight:600}}>
          {error}
        </div>
      )}

      {stopBanner && (
        <div data-testid="journal-stop-banner" style={{
          background: "#dc2626", color: "#fff",
          padding: "12px 16px", borderRadius: 9,
          fontSize: 13, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          animation: "stopFlash 1s ease-in-out infinite",
          boxShadow: "0 4px 16px rgba(220,38,38,0.4)",
        }}>
          <span>🔴 STOP LOSS HIT — <b>{stopBanner.ticker}</b> @ ${stopBanner.current.toFixed(2)} (your stop: ${stopBanner.stop.toFixed(2)}) · EXIT NOW</span>
          <button onClick={() => setStopBanner(null)} style={{background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", padding:"3px 9px", borderRadius:5, fontSize:11, cursor:"pointer", fontWeight:600}}>×</button>
          <style>{`@keyframes stopFlash { 0%, 100% { background: #dc2626; } 50% { background: #991b1b; } }`}</style>
        </div>
      )}

      {view==="journal" && (
        <>
          <div style={T.card}>
            <div style={T.sectionLab}>{editId ? "Edit trade" : "Log new trade"}</div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <FormInput label="Date"   fieldKey="date"   value={form.date}   type="date"   onChange={handleFormChange}/>
              <FormInput label="Time"   fieldKey="time"   value={form.time}   type="time"   onChange={handleFormChange}/>
              <FormInput label="Ticker" fieldKey="ticker" value={form.ticker} type="text"   ph="AAPL" onChange={handleFormChange}/>
              <FormInput label="Entry $" fieldKey="entry" value={form.entry}  type="number" ph="0.00" onChange={handleFormChange}/>
              <FormInput label="Exit $"  fieldKey="exit"  value={form.exit}   type="number" ph="0.00" onChange={handleFormChange}/>
              <FormInput label="Stop $"  fieldKey="stop"  value={form.stop}   type="number" ph="0.00" onChange={handleFormChange}/>
              <FormInput label="Shares"  fieldKey="shares" value={form.shares} type="number" ph="10"   onChange={handleFormChange}/>
              <FormSelect label="Strategy" fieldKey="strategy" value={form.strategy} opts={["Momentum + VWAP","Gap & Go","ORB","BB Squeeze","Order Flow Scalp","VWAP Anchored","Open→Power Hour","MACD Divergence"]} onChange={handleFormChange}/>
              <FormSelect label="Session"  fieldKey="session"  value={form.session}  opts={["Open","Power","Mid"]}                  onChange={handleFormChange}/>
              <FormSelect label="Grade"    fieldKey="grade"    value={form.grade}    opts={["A+","A","B+","B","C","D","F"]}         onChange={handleFormChange}/>
            </div>
            <div style={{ display:"flex", flexDirection:"column", marginTop:10 }}>
              <label style={T.inputLab}>Notes / what happened</label>
              <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Which indicators confirmed? What went right/wrong?"
                data-testid="journal-input-notes"
                style={{ ...T.input, minHeight:60, resize:"vertical" }}/>
            </div>
            <div style={{ display:"flex", gap:8, marginTop:10 }}>
              <button onClick={save} disabled={saving} data-testid="journal-save-btn"
                style={{ ...T.primaryBtn, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}
                onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = "#4338ca"; }}
                onMouseLeave={(e) => { if (!saving) e.currentTarget.style.background = "#4f46e5"; }}
              >+ {saving?"Saving…":(editId?"Update trade":"Log trade")}</button>
              {editId && <button onClick={()=>{setEditId(null);setForm(EMPTY);}} data-testid="journal-cancel-btn" style={T.secondaryBtn}>Cancel</button>}
            </div>
          </div>

          <div style={{ ...T.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"14px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:"1px solid #f1f5f9" }}>
              <div style={{ ...T.sectionLab, marginBottom:0 }}>Trade log</div>
              <span style={T.badgeNeu}>{trades.length} trades · {trades.filter(t=>t.status==="Open").length} open</span>
            </div>
            {loading ? (
              <div data-testid="journal-loading" style={{ padding:"48px 16px", textAlign:"center", fontSize:13, color:"#94a3b8" }}>Loading trades…</div>
            ) : trades.length === 0 ? (
              <div data-testid="journal-empty" style={{ padding:"48px 24px", textAlign:"center" }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#e2e8f0" strokeWidth="1.5" style={{ margin:"0 auto", display:"block" }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                <div style={{ fontSize:14, color:"#94a3b8", marginTop:12 }}>No trades logged yet</div>
                <div style={{ fontSize:12, color:"#cbd5e1", marginTop:4 }}>Use the form above to log your first trade</div>
              </div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead>
                    <tr style={{ background:"#f8fafc", borderBottom:"1px solid #e2e8f0" }}>
                      {["Date","Time","Ticker","Dir","Entry","Exit","Stop","R:R","Shares","Strategy","Session","Grade","P&L",""].map((h,i)=>(
                        <th key={i} style={{ padding:"8px 12px", textAlign:"left", color:"#94a3b8", fontWeight:500, fontSize:10, textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {trades.map(t=>{
                      const pnl = (t.status==="Closed" || t.status==="Stop Hit") ? (t.exit-t.entry)*t.shares : null;
                      const rrT = t.stop && t.exit ? Math.abs((t.exit-t.entry)/(t.entry-t.stop)) : null;
                      // Derive direction display from existing data; no schema change.
                      const dir = t.direction || (t.exit != null && t.exit !== "" ? (t.exit >= t.entry ? "LONG" : "SHORT") : (t.stop != null && t.stop < t.entry ? "LONG" : "SHORT"));
                      const dirIsLong = /LONG|long|Long/.test(dir);
                      const gradeBadge = (() => {
                        const g = (t.grade||"B").toUpperCase();
                        if (g.startsWith("A")) return T.badgePos;
                        if (g.startsWith("B")) return T.badgeIndigo;
                        if (g.startsWith("C")) return { background:"#fef3c7", color:"#d97706", fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4 };
                        return T.badgeNeg;
                      })();
                      const rrColor = rrT == null ? "#94a3b8" : rrT >= 2 ? "#16a34a" : rrT >= 1 ? "#d97706" : "#dc2626";
                      return (
                        <tr key={t.id} data-testid={`journal-row-${t.ticker}`}
                          style={{ borderBottom:"1px solid #f1f5f9" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >
                          <td style={{ padding:"10px 12px", color:"#64748b", whiteSpace:"nowrap", fontSize:12 }}>{t.date}</td>
                          <td style={{ padding:"10px 12px", color:"#64748b", fontFamily:"monospace", fontSize:12 }}>{t.time}</td>
                          <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#4f46e5", fontWeight:600, fontSize:12 }}>{t.ticker}</td>
                          <td style={{ padding:"10px 12px" }}><span style={dirIsLong ? T.badgePos : T.badgeNeg}>{dirIsLong ? "LONG" : "SHORT"}</span></td>
                          <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#1e293b", fontSize:12 }}>${t.entry.toFixed(2)}</td>
                          <td style={{ padding:"10px 12px", fontFamily:"monospace", fontSize:12 }}>{t.exit ? `$${t.exit.toFixed(2)}` : <span style={{ color:"#d97706" }}>Open</span>}{t.status==="Stop Hit" && <span style={{ marginLeft:6, fontSize:9, padding:"1px 6px", borderRadius:4, background:"#fee2e2", color:"#dc2626", fontWeight:700 }}>STOP HIT</span>}</td>
                          <td style={{ padding:"10px 12px", fontFamily:"monospace", color:"#dc2626", fontSize:12 }}>{t.stop ? `$${t.stop.toFixed(2)}` : "—"}</td>
                          <td style={{ padding:"10px 12px", fontFamily:"monospace", color:rrColor, fontWeight:600, fontSize:12 }}>{rrT ? `${rrT.toFixed(1)}×` : "—"}</td>
                          <td style={{ padding:"10px 12px", color:"#1e293b", fontSize:12 }}>{t.shares}</td>
                          <td style={{ padding:"10px 12px", fontSize:11, color:"#4f46e5", fontWeight:500, whiteSpace:"nowrap" }}>{t.strategy}</td>
                          <td style={{ padding:"10px 12px" }}><span style={T.badgeNeu}>{t.session}</span></td>
                          <td style={{ padding:"10px 12px" }}><span style={gradeBadge}>{t.grade||"B"}</span></td>
                          <td style={{ padding:"10px 12px", fontFamily:"monospace", fontWeight:600, color: pnl===null ? "#d97706" : pnl>=0 ? "#16a34a" : "#dc2626", fontSize:12 }}>{pnl===null ? "Live" : (pnl>=0 ? "+" : "")+`$${pnl.toFixed(0)}`}</td>
                          <td style={{ padding:"10px 12px" }}>
                            <div style={{ display:"flex", gap:4 }}>
                              <button onClick={()=>edit(t)} data-testid={`journal-edit-${t.ticker}`} style={{ ...T.secondaryBtn, fontSize:10, padding:"3px 8px" }}>Edit</button>
                              <button onClick={()=>del(t.id)} data-testid={`journal-del-${t.ticker}`} style={{ ...T.badgeNeg, fontSize:10, padding:"3px 8px", border:"none", cursor:"pointer" }}>Del</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {view==="performance" && (
        <>
          <div style={{ display:"flex", gap:6 }}>
            {["All","Open","Power"].map(f=>(
              <button key={f} onClick={()=>setSessF(f)}
                style={{
                  fontSize:12, fontWeight:500, padding:"6px 14px", borderRadius:6, border:"none", cursor:"pointer",
                  background: sessF===f ? "#f1f5f9" : "transparent",
                  color:      sessF===f ? "#0f172a" : "#64748b",
                }}>
                {f==="All"?"All sessions":f==="Open"?"Market Open":"Power Hour"}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div data-testid="journal-perf-empty" style={{ ...T.card, padding:"48px 24px", textAlign:"center", color:"#94a3b8", fontSize:13 }}>
              No closed trades yet. Performance metrics will appear once you log and close trades.
            </div>
          ) : (
            <>
              <div style={T.card}>
                <div style={T.sectionLab}>Key metrics</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:8 }}>
                  {[
                    {l:"Net P&L",      v:`$${total.toFixed(0)}`,                c: total>=0 ? "#16a34a" : "#dc2626"},
                    {l:"Win rate",     v:`${winRate}%`,                          c: winRate>=55 ? "#16a34a" : "#d97706"},
                    {l:"Avg R:R",      v:rr,                                     c:"#0f172a"},
                    {l:"Profit factor",v:pf,                                     c:"#4f46e5"},
                    {l:"Total trades", v:filtered.length,                        c:"#0f172a"},
                    {l:"Avg win",      v:`$${avgW.toFixed(0)}`,                  c:"#16a34a"},
                    {l:"Avg loss",     v:`-$${avgL.toFixed(0)}`,                 c:"#dc2626"},
                    {l:"Win/Loss",     v:`${wins.length}W ${losses.length}L`,    c:"#64748b"},
                  ].map(m=>(
                    <div key={m.l} style={T.statCard}>
                      <div style={{ fontSize:10, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:500, marginBottom:3 }}>{m.l}</div>
                      <div style={{ fontSize:17, fontWeight:700, color:m.c, fontFamily:"monospace" }}>{m.v}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={T.card}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, alignItems:"center" }}>
                  <span style={{ ...T.sectionLab, marginBottom:0 }}>Equity curve</span>
                  <span style={{ fontSize:14, fontWeight:700, color: eqUp ? "#16a34a" : "#dc2626", fontFamily:"monospace" }}>{eqUp ? "+" : ""}{((eqPts[eqPts.length-1]-10000)/10000*100).toFixed(1)}%</span>
                </div>
                <Spark data={eqPts} color={eqUp ? "#16a34a" : "#dc2626"} w={600} h={70}/>
              </div>

              <div style={{ ...T.card, padding:0 }}>
                <div style={{ padding:"14px 20px", borderBottom:"1px solid #f1f5f9" }}>
                  <div style={{ ...T.sectionLab, marginBottom:0 }}>Strategy performance</div>
                </div>
                {Object.entries(byStrat).map(([strat,data],i)=>{
                  const wr=Math.round(data.w/data.n*100);
                  const wrC = wr>=60 ? "#16a34a" : wr>=50 ? "#d97706" : "#dc2626";
                  return(
                    <div key={strat} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 20px", borderTop: i>0 ? "1px solid #f1f5f9" : "none", fontSize:12 }}>
                      <span style={{ fontWeight:600, color:"#0f172a", width:160, flexShrink:0 }}>{strat}</span>
                      <div style={{ flex:1, height:6, background:"#f1f5f9", borderRadius:3 }}>
                        <div style={{ height:"100%", width:`${wr}%`, background:wrC, borderRadius:3 }}/>
                      </div>
                      <span style={{ color:wrC, fontWeight:700, width:38, fontFamily:"monospace" }}>{wr}%</span>
                      <span style={{ color:"#64748b", width:30, fontFamily:"monospace" }}>{data.n}T</span>
                      <span style={{ fontWeight:700, color: data.pnl>=0 ? "#16a34a" : "#dc2626", width:64, textAlign:"right", fontFamily:"monospace" }}>{data.pnl>=0 ? "+" : ""}{data.pnl.toFixed(0)}</span>
                    </div>
                  );
                })}
              </div>

              <div style={T.card}>
                <div style={T.sectionLab}>Trade grades — are you following your rules?</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {["A+","A","B+","B","C","D","F"].map(g=>{
                    const count=filtered.filter(t=>t.grade===g).length;
                    if(!count)return null;
                    const c = gradeC[g] || "#4f46e5";
                    return(
                      <div key={g} style={{ ...T.statCard, textAlign:"center", minWidth:60, background:c+"14" }}>
                        <div style={{ fontSize:18, fontWeight:700, color:c, fontFamily:"monospace" }}>{g}</div>
                        <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{count} trade{count>1?"s":""}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize:11, color:"#94a3b8", marginTop:10 }}>A/B grades = followed the rules. C/D/F = broke rules. Target 80%+ A/B grades.</div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
